import { getCustomer } from "@/graphql/schemas/shared/utils/getCustomer"
import { CommerceService } from "@/services/commerce"

import { internalCustomerException } from "../../customer/utils/customerQueryHelpers"
import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import {
  findCustomerTargetAddress,
  formCustomerCustomFieldsAction,
  mapCustomerAddressInformation,
} from "../utils/customerAddressInformation"

export const mutationSetCustomerAddressStatus = {
  async setCustomerAddressStatus(parent, args, context, info) {
    const { customerId, customerKey, addressId, status, customerNumber } = args

    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.ADDRESSES,
      operation: Operations.UPDATE,
    })

    const customer = await getCustomer({ id: customerId, key: customerKey, number: customerNumber })
    await internalCustomerException({ id: customer.id })

    await validateAccessRightsToCustomer({
      user: user,
      customerNumber: customer.customerNumber,
    })

    let actions = []
    const commerceApi = CommerceService.api()

    let targetStatus = ""
    switch (status) {
      case "DELETED":
        targetStatus = "X"
        break
      case "CLOSED":
        targetStatus = "S"
        break
      default:
        targetStatus = "A"
    }

    // no address was found
    if (customer?.addresses?.length <= 0) return null

    const originalAddress = customer?.addresses?.find((address) => address?.id === addressId)

    if (!originalAddress) return null

    const targetAddress = mapCustomerAddressInformation(originalAddress, customer)

    // first need to remove from shipping address ids list and then set as shipping address again.
    // If the shipping address is the default shipping address, the defaultShippingAddressId is unset
    // https://docs.commercetools.com/api/projects/me-profile#remove-shipping-address-id
    if (targetAddress.isDefaultShipping) {
      actions.push({
        action: "removeShippingAddressId",
        addressId: targetAddress.id,
      })

      actions.push({
        action: "addShippingAddressId",
        addressId: targetAddress.id,
      })
    }

    if (targetAddress.isDefaultBilling) {
      actions.push({
        action: "removeBillingAddressId",
        addressId: targetAddress.id,
      })

      actions.push({
        action: "addBillingAddressId",
        addressId: targetAddress.id,
      })
    }

    // nothing to change in this case
    if (targetAddress?.custom?.fields?.status === targetStatus) {
      return targetAddress
    }

    if (!targetAddress.custom) {
      actions.push(
        await formCustomerCustomFieldsAction(originalAddress, targetStatus, {
          creationAt: new Date(),
          creationBy: user.employeeNumber,
          modifiedBy: user.employeeNumber,
          versionNr: 0,
        }),
      )
    } else {
      const customFields = originalAddress?.custom?.fields

      // need to update status and modification user and dates
      // rest of the fields have to be passed as they are
      actions.push(
        await formCustomerCustomFieldsAction(originalAddress, targetStatus, {
          creationBy: customFields?.creationBy,
          creationAt: customFields?.creationAt,
          modifiedBy: user.employeeNumber,
          versionNr: customFields?.versionNr,
          benefitRecipient: customFields?.benefitRecipient,
        }),
      )
    }

    const payload = {
      version: customer.version,
      actions,
    }

    try {
      const updatedCustomer = (
        await commerceApi
          .customers()
          .withId({ ID: customer.id })
          .post({
            body: JSON.stringify(payload),
          })
          .execute()
      )?.body

      const updatedTargetAddress = findCustomerTargetAddress(updatedCustomer, addressId)

      return updatedTargetAddress
    } catch (e) {
      throw new Error(e)
    }
  },
}
